import { IColumn, IGroup } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactElement, Ref, useCallback, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { IBasicColumn } from '../BasicTable/BasicTable';
import { IColumnWithDataType } from '../DetailsList/DetailsList';
import { DetailsListSizes } from '../DetailsList/DetailsList.styles';
import { IconButton } from '../IconButton/IconButton';
import { iconButtonStylesNoBG } from '../IconButton/IconButton.styles';
import { ListCell } from '../ListCell/ListCell';
import { Loader } from '../Loader/Loader';
import { IListRowClassNames, IListRowStyles, listRowStylesDefault } from './ListRow.styles';

/**
 * When a row needs meta information, we can use it.
 * A row data type can have `__meta__` field
 * example
const rows = [{
  id: '1',
  name: '',
  etc: '',
  __meta__: {
    className: 'custom-row-class-name',
    fieldMeta: {
      id: {
        errorMessage: 'id should be a unique value',
        data: {}
      },
      name: {
        className: 'custom-cell-class-name',
        changed: true,
        errorMessage: 'name is a required field'
      },
      etc: {
        disabled: true
      }
    }
  }
}]; 
 */
export interface RowMeta {
  className?: string; // a custom row class name
  data?: { [key: string]: any }; // a custom data for UI
  fieldMeta?: {
    [key: string]: {
      errorMessage?: string;
      changed?: boolean;
      className?: string;
      disabled?: boolean;
    };
  };
}

export enum ListRowType {
  Default = 'default',
  Basic = 'basic',
  GroupHeaderCard = 'group-header-card',
  GroupHeader = 'group-header',
  Group = 'group',
  Contents = 'Contents',
}

export interface IListRowProps<T extends { [key: string]: any }>
  extends IComponent<IListRowStyles>,
    HTMLAttributes<HTMLDivElement> {
  data: T;
  columns: IColumnWithDataType[] | IBasicColumn[];
  group?: IGroup;
  itemIndex?: number;
  hasLeftOpenClose?: boolean;
  hideHeader?: boolean;
  collapsed?: boolean;
  selected?: boolean;
  title?: string;
  contents?: ReactElement;
  type?: ListRowType;
  loadingMessageForChildRows?: string;
  hasOpenClose?: boolean;
  openCloseByButton?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onOpen?: (data: T, open: boolean) => void;
}

const getColumnWidth = (type: ListRowType, columns: IColumn[] | IBasicColumn[], index: number) => {
  const column = columns[index];
  const width = column.calculatedWidth || column.currentWidth || column.minWidth;
  const rowLeftRightBordersWidth = 2;
  if (type === ListRowType.Basic) {
    return width;
  }
  if (type === ListRowType.Default) {
    return index === 0 || index === columns.length - 1 ? width - rowLeftRightBordersWidth / 2 : width;
  } else {
    const expanderWidth = DetailsListSizes.GroupHeaderExpanderWidth - rowLeftRightBordersWidth;
    if (type === ListRowType.GroupHeaderCard || type === ListRowType.GroupHeader) {
      return index === 0 ? width + expanderWidth : width;
    } else {
      return width - (DetailsListSizes.GroupPaddingLeftRight * 2 - expanderWidth) / columns.length;
    }
  }
};

const getFieldMetaValues = (fieldName?: string, data?: any) => {
  const fieldMeta = data?.__meta__?.fieldMeta;
  if (fieldName && fieldMeta && fieldMeta[fieldName]) {
    const field = fieldMeta[fieldName];
    return {
      errorMessage: field.errorMessage || undefined,
      changed: !!field.changed,
      className: field.className || undefined,
      disabledCell: !!field.disabled,
    };
  }
  return {};
};

const getCustomRowClassName = (data?: any): string => {
  const className = data?.__meta__?.className;
  return className ? ` ${className}` : '';
};

export const ListRow = forwardRef(
  <T extends { [key: string]: any }>(props: IListRowProps<T>, ref: Ref<HTMLDivElement>) => {
    const {
      styles,
      className,
      data,
      columns = [],
      group,
      hasLeftOpenClose,
      selected,
      hideHeader,
      collapsed,
      title,
      contents,
      type = ListRowType.Default,
      loadingMessageForChildRows,
      hasOpenClose,
      openCloseByButton,
      loading,
      disabled,
      itemIndex,
      onOpen,
      onClick,
      ...elementProps
    } = props;
    const [open, setOpen] = useState(!collapsed);
    const cellCount = columns.length;
    const classNames = useClassNames<Partial<IListRowStyles>, IListRowClassNames>(
      'ListRow',
      listRowStylesDefault,
      styles,
      className,
      type && `row-${type}`,
      hasLeftOpenClose && 'has-left-open-close',
      selected && 'is-selected',
      loading && 'is-loading',
      hasOpenClose && !collapsed && 'is-expanded',
      !disabled && (hasOpenClose || onClick) && 'is-clickable',
      disabled && 'is-disabled'
    );
    const onOpenClose = useCallback(() => {
      if (onOpen) {
        onOpen(data, !open);
      }
      setOpen(!open);
    }, [data, open, onOpen]);
    const button = hasOpenClose && (
      <div className={classNames.button}>
        <IconButton
          styles={iconButtonStylesNoBG}
          iconName="ChevronRight"
          className={open ? 'rotate90' : ''}
          disabled={disabled}
          onClick={!disabled && openCloseByButton ? onOpenClose : undefined}
        />
      </div>
    );
    return (
      <div
        {...elementProps}
        ref={ref}
        className={`${classNames.root}${getCustomRowClassName(data)}`}
        data-test={props['data-test'] || 'list-row'}
        onClick={
          !disabled && (onClick || hasOpenClose)
            ? (e) => {
                if (!openCloseByButton) {
                  onOpenClose();
                }
                if (onClick) {
                  onClick(e);
                }
              }
            : undefined
        }
      >
        {loading ? (
          <div className={classNames.row}></div>
        ) : (
          <>
            <div className={classNames.row}>
              {columns.map((column: IColumnWithDataType | IBasicColumn, i: number) => {
                const { errorMessage, changed, className, disabledCell } = getFieldMetaValues(column.fieldName, data);
                return (
                  <ListCell
                    {...column.data?.listCellProps}
                    data={data}
                    first={i === 0}
                    last={i === cellCount - 1}
                    headerText={
                      !hideHeader
                        ? column.data?.headerFieldName
                          ? data[column.data?.headerFieldName]
                          : column.name
                        : undefined
                    }
                    text={
                      column.fieldName && data[column.fieldName] !== undefined ? String(data[column.fieldName]) : ''
                    }
                    errorMessage={errorMessage}
                    changed={changed}
                    key={column.key}
                    selected={selected}
                    width={getColumnWidth(type, columns, i)}
                    group={group}
                    onRender={column.onRender}
                    hasLeftOpenClose={hasLeftOpenClose}
                    column={column}
                    columnIndex={i}
                    rowIndex={itemIndex}
                    disabled={disabled || disabledCell}
                    canDisableCustomContents={disabledCell}
                    className={className}
                  />
                );
              })}
              {button}
            </div>
            {!loadingMessageForChildRows && open && (title || contents) && (
              <div className={classNames.details}>
                {title && <div className={classNames.title}>{title}</div>}
                {contents && <div className={classNames.contents}>{contents}</div>}
              </div>
            )}
            {loadingMessageForChildRows && open && (
              <div className={classNames.loaderForChildRows}>
                <Loader label={loadingMessageForChildRows} />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
) as <T extends { [key: string]: any }>(props: IListRowProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
